import React from 'react';
import { API } from '../api';
import { Section } from './ui';

/*
 * What each AI helper is told.
 *
 * Newsletters and posters go out in the club's name, and most of their words
 * and pictures are now written by a model. An admin answerable for them
 * should be able to read what that model is told, so this shows it: each
 * helper's standing instructions exactly as they are sent, and beside them
 * the parts that come from this club's own settings.
 *
 * Read-only. The settings it lists are changed where they always were — the
 * Newsletter and Posters tabs — and the instructions themselves are not
 * editable here.
 */

const wordCount = s => String(s || '').trim().split(/\s+/).filter(Boolean).length;

/* The instructions are kept in the code broken at about 78 characters, which
   on a phone wraps into a ragged column. For reading, the lines of each
   paragraph are joined back up; a blank line still ends a paragraph and a
   "- " bullet still starts a line of its own. Display only — the text sent
   to the model is untouched. */
const reflow = text => String(text || '').split('\n').reduce((out, raw) => {
  const line = raw.trim();
  const prev = out.length ? out[out.length - 1] : null;
  const startsOwnLine = !line || /^[-•]\s/.test(line) || /^\d+\.\s/.test(line);
  if (prev === null || prev === '' || startsOwnLine) out.push(line);
  else out[out.length - 1] = prev + ' ' + line;
  return out;
}, []).join('\n');

function Assistant({ a }) {
  return (
    <article className="ai-assistant">
      <h3 className="ai-assistant-name">{a.name}</h3>
      <p className="ai-assistant-purpose">{a.purpose}</p>
      <p className="ai-assistant-meta">
        <span><i className="fa fa-hand-pointer-o"></i> {a.when}</span>
        <span><i className="fa fa-microchip"></i> {a.model}</span>
      </p>

      {a.clubAdds.length > 0 && (
        <div className="ai-assistant-adds">
          <span className="gs-section-label">From your club's settings</span>
          <dl>
            {a.clubAdds.map(x => (
              <React.Fragment key={x.label}>
                <dt>{x.label}</dt>
                <dd>{x.text}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      )}

      {a.alsoSent && (
        <p className="ai-assistant-also">
          <strong>Also sent each time:</strong> {a.alsoSent}
        </p>
      )}

      {a.instructions && (
        <details className="ai-assistant-prompt">
          <summary>What it is told <span>({wordCount(a.instructions)} words)</span></summary>
          <pre>{reflow(a.instructions)}</pre>
        </details>
      )}
      {a.example && (
        <details className="ai-assistant-prompt">
          <summary>An example, built from your club's settings <span>({wordCount(a.example)} words)</span></summary>
          <pre>{a.example}</pre>
        </details>
      )}
    </article>
  );
}

function AiAssistantsTab({ clubId }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setError('');
      const r = await fetch(`${API}/api/ai-assistants${clubId ? '?clubId=' + clubId : ''}`);
      const body = await r.json().catch(() => ({}));
      if (cancelled) return;
      if (!r.ok) setError(body.error || 'Could not load the AI assistants.');
      else setData(body);
    })();
    return () => { cancelled = true; };
  }, [clubId]);

  if (error) return <div className="w3-panel w3-pale-red w3-leftbar w3-border-red"><p>{error}</p></div>;
  if (!data) return <p className="w3-text-grey">Loading…</p>;

  const areas = [...new Set(data.assistants.map(a => a.area))];

  return (
    <div className="ai-assistants">
      <p className="ai-assistants-intro">
        These are the AI helpers that write your newsletters and design your posters, and
        exactly what each one is told. The parts that come from your club are changed on the
        Newsletter and Posters tabs. Every one of them is told never to invent a date, time,
        price or result, and to treat names members have typed as information, not instructions.
      </p>
      {areas.map(area => (
        <Section key={area} label={area}>
          {data.assistants.filter(a => a.area === area).map(a => <Assistant key={a.id} a={a} />)}
        </Section>
      ))}
    </div>
  );
}

export default AiAssistantsTab;
